/**
 * BottomToolbar Component
 *
 * A reusable bottom toolbar component for page-level actions.
 * Based on Material Design 3 specifications and WCAG accessibility standards.
 *
 * Features:
 * - 8px grid aligned dimensions (56px height, 48px touch targets)
 * - Semantic HTML with role="toolbar" and ARIA attributes
 * - Roving tabindex keyboard navigation (arrow keys)
 * - Position variants: fixed, sticky, static
 * - iOS safe area support for notched devices
 * - Mobile landscape optimization (48px height)
 * - Overlay mode: spans full width over sidebar and content
 *
 * @example Basic usage
 * ```tsx
 * <BottomToolbar aria-label="View controls">
 *   <ButtonTabs options={viewModes} selected={mode} onSelect={setMode} />
 *   <Toggle textOff="Hide" textOn="Show" checked={show} onChange={setShow} />
 *   <Button variant="ghost" size="sm">Help</Button>
 * </BottomToolbar>
 * ```
 *
 * @example Overlay mode with PanelSplitView
 * ```tsx
 * // In a component with PanelSplitView
 * const [collapsed, setCollapsed] = useState(false);
 * const contentOffset = collapsed ? "0" : "24em";
 *
 * <BottomToolbar
 *   overlay
 *   contentOffset={contentOffset}
 *   aria-label="View controls"
 * >
 *   <ButtonTabs options={viewModes} selected={mode} onSelect={setMode} />
 * </BottomToolbar>
 * ```
 */

import React, { useRef, useCallback } from "react";
import { cx } from "@/utils/cx";
import type { BottomToolbarProps } from "./types";
import styles from "./BottomToolbar.module.css";

export const BottomToolbar: React.FC<BottomToolbarProps> = ({
  children,
  position = "static",
  align = "space-between",
  "aria-label": ariaLabel = "Toolbar",
  showBorder = true,
  overlay = false,
  contentOffset = "0",
  className = "",
  "data-testid": testId,
}) => {
  const toolbarRef = useRef<HTMLDivElement>(null);

  /**
   * Handle keyboard navigation within the toolbar.
   * Implements roving tabindex pattern per WAI-ARIA toolbar pattern.
   *
   * - Left/Right Arrow: Navigate between focusable elements
   * - Home: Jump to first focusable element
   * - End: Jump to last focusable element
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    // Get all focusable elements within the toolbar
    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(
      toolbar.querySelectorAll<HTMLElement>(focusableSelector),
    ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);

    if (focusableElements.length === 0) return;

    const currentIndex = focusableElements.indexOf(
      document.activeElement as HTMLElement,
    );

    // Only handle navigation if focus is within the toolbar
    if (currentIndex === -1) return;

    let nextIndex: number | null = null;

    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        nextIndex = (currentIndex + 1) % focusableElements.length;
        break;
      case "ArrowLeft":
        event.preventDefault();
        nextIndex =
          (currentIndex - 1 + focusableElements.length) %
          focusableElements.length;
        break;
      case "Home":
        event.preventDefault();
        nextIndex = 0;
        break;
      case "End":
        event.preventDefault();
        nextIndex = focusableElements.length - 1;
        break;
      default:
        return;
    }

    if (nextIndex !== null) {
      focusableElements[nextIndex].focus();
    }
  }, []);

  const toolbarClasses = cx(styles.toolbar, className);

  // In overlay mode, render a wrapper with pointer-events: none
  // and content area that follows the sidebar offset
  if (overlay) {
    return (
      <div
        ref={toolbarRef}
        role="toolbar"
        aria-label={ariaLabel}
        aria-orientation="horizontal"
        className={toolbarClasses}
        data-position="fixed"
        data-overlay="true"
        data-border={showBorder ? "true" : undefined}
        data-testid={testId}
        onKeyDown={handleKeyDown}
      >
        <div
          className={styles.overlayContent}
          data-align={align}
          style={{ marginLeft: contentOffset }}
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={toolbarRef}
      role="toolbar"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className={toolbarClasses}
      data-position={position}
      data-align={align}
      data-border={showBorder ? "true" : undefined}
      data-testid={testId}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

BottomToolbar.displayName = "BottomToolbar";
